import React, { useState } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Stack,
  Chip,
  TextField,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Google as GoogleIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const { loginWithGoogle, loginWithEmail } = useAuth();
  
  // State for email/password login
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleGoogleLogin = () => {
    console.log('🟡 LoginPage: Google login button clicked');
    
    // Environment-aware URL generation (consistent with GoogleAuthView.js)
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? `${window.location.protocol}//${window.location.hostname}`
      : `${window.location.protocol}//${window.location.hostname}:5001`;
    const authUrl = `${baseUrl}/api/auth/google`;
    console.log('🟡 Redirecting to:', authUrl);
    
    // Immediate redirect
    window.location.href = authUrl;
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Bitte geben Sie E-Mail und Passwort ein');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await loginWithEmail(email, password);
    } catch (err) {
      setError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: amDesignColors.white,
        py: 8
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center">
          {/* Left: Hero Content */}
          <Grid item xs={12} md={7}>
            <Stack spacing={4}>
              {/* Main Headline */}
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: '#1a1a1a',
                    mb: 2
                  }}
                >
                  Werde zum LokalMeister in deiner Region
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    fontWeight: 400,
                    color: amDesignColors.text.secondary,
                    lineHeight: 1.5
                  }}
                >
                  A&M LokalMeister macht dich zum Marktführer in deiner Region. Schluss mit leeren Auftragsbüchern!
                </Typography>
              </Box>

              {/* Benefits */}
              <Stack spacing={3}>
                {[
                  'In 3 Wochen auf Seite 1 bei Google',
                  'Bis zu 300% mehr Kundenanfragen',
                  'Automatische Optimierung - ohne Aufwand',
                  'Höhere Preise als LokalMeister durchsetzen'
                ].map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon sx={{ color: '#22c55e', fontSize: 24 }} />
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#1a1a1a' }}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Social Proof */}
              <Box sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} sx={{ color: '#fbbf24', fontSize: 20 }} />
                  ))}
                  <Typography variant="body2" sx={{ fontWeight: 600, ml: 1, color: '#1a1a1a' }}>
                    4.9/5 Sterne
                  </Typography>
                  <Typography variant="body2" sx={{ color: amDesignColors.text.secondary, ml: 1 }}>
                    (2.847 LokalMeister)
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: amDesignColors.text.secondary }}>
                  "Seit 3 Wochen auf Platz 1 bei Google. Mein Telefon steht nicht mehr still!"
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Klaus M., Sanitärbetrieb Hamburg
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Right: CTA Section */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                bgcolor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: 6,
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a', mb: 2 }}>
                Kostenlos starten
              </Typography>
              
              <Typography variant="body1" sx={{ color: amDesignColors.text.secondary, mb: 4, lineHeight: 1.6 }}>
                30 Sekunden Analyse + 30 Tage kostenlos testen
              </Typography>

              {!showEmailLogin ? (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<GoogleIcon />}
                    onClick={handleGoogleLogin}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      bgcolor: '#C41E3A',
                      color: amDesignColors.white,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      mb: 3,
                      '&:hover': {
                        bgcolor: '#A01729'
                      }
                    }}
                  >
                    Mit Google anmelden
                  </Button>
                  
                  <Divider sx={{ my: 2 }}>oder</Divider>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<EmailIcon />}
                    onClick={() => setShowEmailLogin(true)}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderColor: '#C41E3A',
                      color: '#C41E3A',
                      borderRadius: 1.5,
                      textTransform: 'none',
                      mb: 4,
                      '&:hover': {
                        borderColor: '#A01729',
                        color: '#A01729',
                        bgcolor: 'rgba(196, 30, 58, 0.04)'
                      }
                    }}
                  >
                    Mit E-Mail anmelden
                  </Button>
                </>
              ) : (
                <Box component="form" onSubmit={handleEmailLogin} sx={{ mb: 4 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <TextField
                    fullWidth
                    label="E-Mail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: amDesignColors.text.secondary }} />
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Passwort"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: <LockIcon sx={{ mr: 1, color: amDesignColors.text.secondary }} />
                    }}
                  />
                  
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      bgcolor: '#C41E3A',
                      color: amDesignColors.white,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      mb: 2,
                      '&:hover': {
                        bgcolor: '#A01729'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Anmelden'}
                  </Button>
                  
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setShowEmailLogin(false);
                      setEmail('');
                      setPassword('');
                      setError('');
                    }}
                    sx={{
                      color: amDesignColors.text.secondary,
                      textTransform: 'none',
                      mb: 2
                    }}
                  >
                    Zurück zu Google Login
                  </Button>
                </Box>
              )}

              <Stack spacing={2} sx={{ mb: 4 }}>
                <Typography variant="caption" sx={{ color: amDesignColors.text.secondary }}>
                  ✓ Keine Kreditkarte nötig ✓ Sofortige Ergebnisse
                </Typography>
              </Stack>

              <Box sx={{ pt: 3, borderTop: '1px solid #e5e7eb' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
                  Danach nur 97€/Monat
                </Typography>
                <Typography variant="caption" sx={{ color: amDesignColors.text.secondary }}>
                  Weniger als 3,50€ pro Tag für konstante Neukunden
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom: More Social Proof */}
        <Box sx={{ mt: 12, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 6 }}>
            Was unsere Kunden sagen
          </Typography>
          
          <Grid container spacing={6}>
            {[
              {
                quote: "280% mehr Kundenanfragen in nur 4 Wochen. Bin ausgebucht bis März!",
                author: "Sarah T., Malerbetrieb München"
              },
              {
                quote: "Meine Preise um 35% erhöht - trotzdem mehr Aufträge denn je!",
                author: "Thomas K., Tischlerei Berlin"
              },
              {
                quote: "Endlich volle Auftragsbücher ohne teure Werbung!",
                author: "M. Weber, Elektriker"
              }
            ].map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    p: 4,
                    bgcolor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    height: '100%'
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 3, color: '#1a1a1a', lineHeight: 1.6 }}>
                    "{testimonial.quote}"
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: amDesignColors.text.secondary }}>
                    {testimonial.author}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;